import React, { useState, useEffect } from "react";
import { Card, Row, Col, Container, Badge, Table, Accordion } from "react-bootstrap";
import Footer from "../layouts/Footer";
import { useAuth } from "../hooks/useAuth";
import { motion } from "framer-motion";

export default function Documentation() {
  const { user, business } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  // Endpoints de l'API
  const endpoints = [
    { method: "GET",  path: "/v1/messages",          auth: "Bearer token JWT", description: "Liste des messages" },
    { method: "GET",  path: "/v1/messages/{id}",     auth: "Bearer token JWT", description: "Détails d'un message" },
    { method: "POST", path: "/v1/messages/send",     auth: "Bearer token JWT", description: "Envoyer un message" },
    { method: "PUT",  path: "/v1/messages/update",   auth: "Bearer token JWT", description: "Mettre à jour un message" },
    { method: "PUT",  path: "/v1/messages/cancel",   auth: "Bearer token JWT", description: "Annuler une file d'attente" },
    { method: "GET",  path: "/v1/account",           auth: "Bearer token JWT", description: "Informations du compte" }
  ];

  // Statuts de transaction
  const transactionStatus = [
    { http: 200, status: "SUCCESS", description: "Transaction réussie" },
    { http: 202, status: "PENDING", description: "Transaction acceptée et en attente" },
    { http: 400, status: "FAILED",  description: "Transaction échouée (raisons dans le message)" }
  ];

  // Codes HTTP
  const httpCodes = [
    { code: 200, description: "OK" },
    { code: 201, description: "Created" },
    { code: 202, description: "Accepted — requête acceptée et mise en file d'attente" },
    { code: 400, description: "Bad Request" },
    { code: 401, description: "Authentication failure" },
    { code: 403, description: "Forbidden" },
    { code: 404, description: "Resource not found" },
    { code: 405, description: "Method Not Allowed" },
    { code: 409, description: "Conflict — duplicate request ID" },
    { code: 500, description: "Internal Server Error" },
    { code: 503, description: "Service Unavailable" }
  ];

  return (
    <div className="doc-page main main-app p-3 p-lg-4">
      <Container fluid>
        {/* ========== HEADER ========== */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="doc-header mb-4"
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="doc-header-icon">
                <i className="bi bi-book"></i>
              </div>
              <div>
                <h3 className="doc-header-title mb-1">Documentation</h3>
                <p className="doc-header-subtitle mb-0">
                  Guides et ressources pour vous accompagner
                </p>
              </div>
            </div>
            <div className="doc-status-badge">
              <span className="doc-status-dot"></span>
              <span>API SMS v1.0</span>
            </div>
          </div>
        </motion.div>

        {/* ========== HERO PRINCIPAL ========== */}
      

        {/* ========== API OVERVIEW ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-info-circle-fill"></i>
                <span>1. API Overview</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6} lg={4}>
              <div className="doc-feature-card doc-feature-blue">
                <div className="doc-feature-header">
                  <div className="doc-feature-icon doc-feature-icon-blue">
                    <i className="bi bi-globe"></i>
                  </div>
                  <span className="doc-feature-badge doc-feature-badge-blue">
                    <i className="bi bi-link-45deg"></i>
                    <span>Base URL</span>
                  </span>
                </div>
                <h6 className="doc-feature-title">URL de base</h6>
                <p className="doc-feature-description mb-2">
                  Toutes les requêtes doivent être dirigées vers :
                </p>
                <code className="doc-code-inline">https://api.minerve-tech.com</code>
              </div>
            </Col>

            <Col md={6} lg={4}>
              <div className="doc-feature-card doc-feature-violet">
                <div className="doc-feature-header">
                  <div className="doc-feature-icon doc-feature-icon-violet">
                    <i className="bi bi-tag"></i>
                  </div>
                  <span className="doc-feature-badge doc-feature-badge-violet">
                    <i className="bi bi-code"></i>
                    <span>Version</span>
                  </span>
                </div>
                <h6 className="doc-feature-title">Version de l'API</h6>
                <p className="doc-feature-description mb-2">
                  La version actuelle de l'API est :
                </p>
                <code className="doc-code-inline">/v1</code>
              </div>
            </Col>

            <Col md={6} lg={4}>
              <div className="doc-feature-card doc-feature-green">
                <div className="doc-feature-header">
                  <div className="doc-feature-icon doc-feature-icon-green">
                    <i className="bi bi-shield-lock"></i>
                  </div>
                  <span className="doc-feature-badge doc-feature-badge-green">
                    <i className="bi bi-key"></i>
                    <span>Auth</span>
                  </span>
                </div>
                <h6 className="doc-feature-title">Authentification</h6>
                <p className="doc-feature-description mb-2">
                  JWT requis dans le header de chaque requête.
                </p>
                <code className="doc-code-inline">Authorization: Bearer &lt;token&gt;</code>
              </div>
            </Col>
          </Row>
        </motion.div>

        {/* ========== AUTHENTICATION ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-shield-check"></i>
                <span>2. Authentication</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={8}>
              <Card className="doc-content-card border-0">
                <Card.Body className="p-4">
                  <p className="doc-text mb-3">
                    L'authentification à l'API utilise <strong>JWT</strong> et doit être
                    envoyée dans le header. Le JWT est généré depuis le portail dans la
                    section développeur et doit être utilisé dans toutes les requêtes.
                  </p>
                  <div className="doc-code-block">
                    <div className="doc-code-header">
                      <span className="doc-code-lang">HTTP Header</span>
                      <button className="doc-code-copy" title="Copier">
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                    <pre className="doc-code-content">
{`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
Content-Type: application/json`}
                    </pre>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <div className="doc-info-banner h-100">
                <div className="doc-info-icon">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                </div>
                <div className="doc-info-content">
                  <div className="doc-info-title">Important</div>
                  <div className="doc-info-text">
                    Les credentials d'authentification doivent rester confidentiels.
                    Ne les exposez jamais côté client.
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </motion.div>

        {/* ========== SEND MESSAGE ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-send-fill"></i>
                <span>3. Envoyer un message</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={12}>
              <Card className="doc-content-card border-0">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="doc-method-badge doc-method-post">POST</span>
                    <code className="doc-endpoint-path">/v1/messages/send</code>
                  </div>

                  <p className="doc-text mb-3">Corps de la requête :</p>

                  <div className="doc-code-block">
                    <div className="doc-code-header">
                      <span className="doc-code-lang">JSON</span>
                      <button className="doc-code-copy" title="Copier">
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                    <pre className="doc-code-content">
{`{
  "message": "smpp moov test",
  "sender_id": "MYCOMPANY",
  "msisdn": "2417000000",
  "external_id": 12598
}`}
                    </pre>
                  </div>

                  <div className="doc-params-table mt-4">
                    <Table responsive borderless className="mb-0">
                      <thead>
                        <tr>
                          <th>Paramètre</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><code>message</code></td>
                          <td><Badge bg="light" text="dark">string</Badge></td>
                          <td>Contenu du message SMS</td>
                        </tr>
                        <tr>
                          <td><code>sender_id</code></td>
                          <td><Badge bg="light" text="dark">string</Badge></td>
                          <td>Identifiant de l'expéditeur (max 11 caractères)</td>
                        </tr>
                        <tr>
                          <td><code>msisdn</code></td>
                          <td><Badge bg="light" text="dark">string</Badge></td>
                          <td>Numéro de téléphone du destinataire</td>
                        </tr>
                        <tr>
                          <td><code>external_id</code></td>
                          <td><Badge bg="light" text="dark">integer</Badge></td>
                          <td>Identifiant unique côté client</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>

        {/* ========== ENDPOINT SUMMARY ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-list-check"></i>
                <span>7. Endpoint Summary</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={12}>
              <Card className="doc-content-card border-0">
                <Card.Body className="p-0">
                  <Table responsive hover className="doc-endpoints-table mb-0">
                    <thead>
                      <tr>
                        <th>Méthode</th>
                        <th>Endpoint</th>
                        <th>Authentification</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {endpoints.map((ep, i) => (
                        <tr key={i}>
                          <td>
                            <span className={`doc-method-badge doc-method-${ep.method.toLowerCase()}`}>
                              {ep.method}
                            </span>
                          </td>
                          <td><code>{ep.path}</code></td>
                          <td><span className="doc-auth-badge"><i className="bi bi-shield-lock"></i> {ep.auth}</span></td>
                          <td className="doc-table-desc">{ep.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>

        {/* ========== IP WHITELIST ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-hdd-network"></i>
                <span>8. IP Whitelist</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={7}>
              <Card className="doc-content-card border-0 h-100">
                <Card.Body className="p-4">
                  <p className="doc-text mb-3">
                    L'accès à l'API est restreint aux adresses IP autorisées pour
                    l'entreprise. Tous les identifiants appartenant à la même entreprise
                    héritent de la liste blanche IP de l'entreprise. Les requêtes
                    provenant d'une adresse IP non autorisée sont rejetées.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="doc-content-card border-0 h-100">
                <Card.Body className="p-4">
                  <div className="doc-code-block mb-3">
                    <div className="doc-code-header">
                      <span className="doc-code-lang">Réponse d'erreur</span>
                    </div>
                    <pre className="doc-code-content doc-code-error">
{`{
  "success": false,
  "error": "IP address not allowed"
}`}
                    </pre>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="doc-http-badge doc-http-403">403</span>
                    <span className="doc-text-small">Forbidden</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>

        {/* ========== TRANSACTION STATUS ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-arrow-left-right"></i>
                <span>9. Transactions Status Codes</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={12}>
              <Card className="doc-content-card border-0">
                <Card.Body className="p-0">
                  <Table responsive borderless className="doc-status-table mb-0">
                    <thead>
                      <tr>
                        <th>HTTP Code</th>
                        <th>Final Status</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionStatus.map((s, i) => (
                        <tr key={i}>
                          <td>
                            <span className={`doc-http-badge doc-http-${s.http}`}>{s.http}</span>
                          </td>
                          <td>
                            <span className={`doc-tx-status doc-tx-${s.status.toLowerCase()}`}>
                              {s.status}
                            </span>
                          </td>
                          <td className="doc-table-desc">{s.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>

        {/* ========== HTTP STATUS CODES ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-code-slash"></i>
                <span>10. HTTP Status Codes</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={12}>
              <Card className="doc-content-card border-0">
                <Card.Body className="p-0">
                  <Table responsive borderless className="doc-status-table mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "120px" }}>Code</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {httpCodes.map((c, i) => (
                        <tr key={i}>
                          <td>
                            <span className={`doc-http-badge doc-http-${c.code}`}>{c.code}</span>
                          </td>
                          <td className="doc-table-desc">{c.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>

        {/* ========== API CONVENTIONS ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-bookmark-check"></i>
                <span>11. API Conventions</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={12}>
              <Card className="doc-content-card border-0">
                <Card.Body className="p-4">
                  <ul className="doc-conventions-list">
                    <li>
                      <i className="bi bi-check-circle-fill"></i>
                      <span>Toutes les réponses sont retournées au format <strong>JSON</strong>.</span>
                    </li>
                    <li>
                      <i className="bi bi-check-circle-fill"></i>
                      <span>Toute communication API doit utiliser <strong>HTTPS</strong>.</span>
                    </li>
                    <li>
                      <i className="bi bi-check-circle-fill"></i>
                      <span>Les identifiants d'authentification doivent être gardés <strong>confidentiels</strong>.</span>
                    </li>
                    <li>
                      <i className="bi bi-check-circle-fill"></i>
                      <span>Une référence de message (<code>msg_reference</code>) doit être <strong>unique</strong> pour le client.</span>
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>

        {/* ========== MESSAGE CALLBACK ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.65 }}
        >
          <Row className="mb-3">
            <Col>
              <div className="doc-section-title">
                <i className="bi bi-arrow-repeat"></i>
                <span>6. Message Callback</span>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={12}>
              <div className="doc-info-banner">
                <div className="doc-info-icon">
                  <i className="bi bi-bell-fill"></i>
                </div>
                <div className="doc-info-content">
                  <div className="doc-info-title">Callback de message</div>
                  <div className="doc-info-text">
                    Un callback est envoyé lorsque la transaction a été traitée.
                    Configurez votre URL de callback depuis le portail développeur.
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </motion.div>

        {/* ========== BANNIÈRE INFO ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <div className="doc-info-banner">
            <div className="doc-info-icon">
              <i className="bi bi-lightbulb-fill"></i>
            </div>
            <div className="doc-info-content">
              <div className="doc-info-title">Une question ou une suggestion ?</div>
              <div className="doc-info-text">
                Cette documentation peut être modifiée à tout moment. En cas d'erreur
                ou de retour, contactez-nous à contact@minerve-tech.com.
              </div>
            </div>
            <a
              href="mailto:contact@minerve-tech.com?subject=Question Documentation API"
              className="doc-info-btn"
            >
              <i className="bi bi-chat-dots"></i>
              <span>Nous contacter</span>
            </a>
          </div>
        </motion.div>

        <Footer />
      </Container>

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .doc-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .doc-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .doc-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .doc-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(102, 126, 234, 0.5);
        }
        .doc-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .doc-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        .doc-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          color: #065f46;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 700;
          border: 1px solid #6ee7b7;
        }
        .doc-status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
          animation: doc-pulse 1.5s ease-in-out infinite;
        }

        /* ========== HERO ========== */
        .doc-hero-card {
          border-radius: 20px !important;
          background: white;
          box-shadow: 0 20px 45px -15px rgba(102, 126, 234, 0.15);
          position: relative;
        }
        .doc-hero-topbar {
          height: 6px;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #06b6d4 100%);
        }
        .doc-particles {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          overflow: hidden;
          pointer-events: none;
          border-radius: 20px;
        }
        .doc-particle {
          position: absolute;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(102,126,234,0.15) 0%, transparent 70%);
          animation: doc-float 8s ease-in-out infinite;
        }
        .doc-particle-1 {
          width: 200px; height: 200px;
          top: -50px; left: -50px;
          animation-delay: 0s;
        }
        .doc-particle-2 {
          width: 280px; height: 280px;
          bottom: -80px; right: -80px;
          background: radial-gradient(circle, rgba(118,75,162,0.12) 0%, transparent 70%);
          animation-delay: 2s;
        }
        .doc-particle-3 {
          width: 150px; height: 150px;
          top: 40%; right: 10%;
          background: radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%);
          animation-delay: 4s;
        }

        /* Hero Icon */
        .doc-hero-icon-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 32px;
        }
        .doc-hero-icon-halo {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 180px;
          height: 180px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(102,126,234,0.15) 0%, transparent 70%);
          animation: doc-halo 3s ease-in-out infinite;
        }
        .doc-hero-icon {
          position: relative;
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: linear-gradient(135deg, #eff6ff 0%, #ede9fe 100%);
          border: 3px solid white;
          box-shadow: 0 15px 35px -10px rgba(102, 126, 234, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 4rem;
          color: #667eea;
        }
        .doc-hero-badge {
          position: absolute;
          bottom: 4px;
          right: -10px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 8px 14px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          box-shadow: 0 8px 20px -6px rgba(16, 185, 129, 0.5);
          border: 2px solid white;
        }

        /* Hero Title */
        .doc-hero-title {
          font-size: 2rem;
          letter-spacing: -0.02em;
        }
        .doc-gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          WebkitBackgroundClip: text;
          WebkitTextFillColor: transparent;
          background-clip: text;
        }
        .doc-hero-description {
          color: #64748b;
          font-size: 1.05rem;
          max-width: 680px;
          line-height: 1.6;
        }

        /* Tags */
        .doc-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #059669;
          border-radius: 10px;
          font-size: 0.8rem;
          font-weight: 600;
          border: 1px solid #86efac;
        }
        .doc-tag i {
          font-size: 0.9rem;
        }

        /* Separator */
        .doc-separator {
          display: flex;
          align-items: center;
          gap: 12px;
          max-width: 260px;
          margin: 28px auto;
        }
        .doc-separator span {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
        }
        .doc-separator i {
          color: #667eea;
          font-size: 1rem;
        }

        /* Support Card */
        .doc-support-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%);
          border-radius: 14px;
          border: 1px solid #e0e7ff;
          max-width: 560px;
          flex-wrap: wrap;
          text-align: left;
        }
        .doc-support-icon {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .doc-support-content {
          flex: 1;
          min-width: 180px;
        }
        .doc-support-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 2px;
        }
        .doc-support-text {
          color: #64748b;
          font-size: 0.8rem;
        }
        .doc-support-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white !important;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.85rem;
          text-decoration: none;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .doc-support-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(102, 126, 234, 0.5);
          color: white !important;
        }

        /* ========== SECTION TITLE ========== */
        .doc-section-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.05rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .doc-section-title i {
          color: #667eea;
          font-size: 1.2rem;
        }

        /* ========== CONTENT CARD ========== */
        .doc-content-card {
          border-radius: 16px !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          border: 1px solid #e2e8f0 !important;
          overflow: hidden;
        }
        .doc-text {
          color: #334155;
          font-size: 0.9rem;
          line-height: 1.65;
        }
        .doc-text-small {
          color: #64748b;
          font-size: 0.8rem;
        }

        /* ========== CODE BLOCKS ========== */
        .doc-code-block {
          background: #0f172a;
          border-radius: 12px;
          overflow: hidden;
        }
        .doc-code-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 16px;
          background: #1e293b;
          border-bottom: 1px solid #334155;
        }
        .doc-code-lang {
          color: #94a3b8;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .doc-code-copy {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 0.85rem;
          transition: color 0.2s;
        }
        .doc-code-copy:hover {
          color: #e2e8f0;
        }
        .doc-code-content {
          margin: 0;
          padding: 16px 20px;
          color: #e2e8f0;
          font-size: 0.82rem;
          line-height: 1.6;
          font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
          white-space: pre;
          overflow-x: auto;
        }
        .doc-code-error {
          color: #fca5a5;
        }
        .doc-code-inline {
          display: inline-block;
          background: #f1f5f9;
          color: #667eea;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.78rem;
          font-family: 'SF Mono', Monaco, monospace;
          border: 1px solid #e2e8f0;
        }

        /* ========== METHOD BADGES ========== */
        .doc-method-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 0.03em;
          min-width: 52px;
        }
        .doc-method-get    { background: #dbeafe; color: #1e40af; }
        .doc-method-post   { background: #d1fae5; color: #065f46; }
        .doc-method-put    { background: #fef3c7; color: #92400e; }
        .doc-method-delete { background: #fee2e2; color: #991b1b; }

        .doc-endpoint-path {
          background: #f1f5f9;
          color: #0f172a;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.82rem;
          font-family: 'SF Mono', Monaco, monospace;
          border: 1px solid #e2e8f0;
        }

        /* ========== PARAMS TABLE ========== */
        .doc-params-table thead th,
        .doc-endpoints-table thead th,
        .doc-status-table thead th {
          background: #f8fafc;
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 12px 16px;
          border-bottom: 1px solid #e2e8f0;
        }
        .doc-params-table tbody td,
        .doc-endpoints-table tbody td,
        .doc-status-table tbody td {
          padding: 12px 16px;
          font-size: 0.84rem;
          color: #334155;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        .doc-params-table tbody tr:last-child td,
        .doc-endpoints-table tbody tr:last-child td,
        .doc-status-table tbody tr:last-child td {
          border-bottom: none;
        }
        .doc-endpoints-table tbody tr:hover,
        .doc-status-table tbody tr:hover {
          background: #f8fafc;
        }
        .doc-table-desc {
          color: #64748b !important;
        }

        /* ========== BADGES ========== */
        .doc-auth-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          background: #ede9fe;
          color: #5b21b6;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 600;
        }

        .doc-http-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 800;
          font-family: 'SF Mono', Monaco, monospace;
          min-width: 48px;
        }
        .doc-http-200 { background: #d1fae5; color: #065f46; }
        .doc-http-201 { background: #d1fae5; color: #065f46; }
        .doc-http-202 { background: #fef3c7; color: #92400e; }
        .doc-http-400 { background: #fee2e2; color: #991b1b; }
        .doc-http-401 { background: #fee2e2; color: #991b1b; }
        .doc-http-403 { background: #fee2e2; color: #991b1b; }
        .doc-http-404 { background: #fee2e2; color: #991b1b; }
        .doc-http-405 { background: #fee2e2; color: #991b1b; }
        .doc-http-409 { background: #fef3c7; color: #92400e; }
        .doc-http-500 { background: #fee2e2; color: #991b1b; }
        .doc-http-503 { background: #fee2e2; color: #991b1b; }

        .doc-tx-status {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.03em;
        }
        .doc-tx-success { background: #d1fae5; color: #065f46; }
        .doc-tx-pending { background: #fef3c7; color: #92400e; }
        .doc-tx-failed  { background: #fee2e2; color: #991b1b; }

        /* ========== CONVENTIONS LIST ========== */
        .doc-conventions-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .doc-conventions-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          font-size: 0.9rem;
          line-height: 1.5;
        }
        .doc-conventions-list li:last-child {
          border-bottom: none;
        }
        .doc-conventions-list li i {
          color: #10b981;
          font-size: 1rem;
          margin-top: 2px;
          flex-shrink: 0;
        }
        .doc-conventions-list li code {
          background: #f1f5f9;
          color: #667eea;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.8rem;
        }

        /* ========== FEATURE CARDS ========== */
        .doc-feature-card {
          background: white;
          border-radius: 16px;
          padding: 22px;
          border: 1px solid #e2e8f0;
          height: 100%;
          transition: all 0.25s ease;
          position: relative;
          overflow: hidden;
        }
        .doc-feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          transition: width 0.3s;
        }
        .doc-feature-blue::before   { background: linear-gradient(180deg, #3b82f6, #2563eb); }
        .doc-feature-violet::before { background: linear-gradient(180deg, #8b5cf6, #7c3aed); }
        .doc-feature-green::before  { background: linear-gradient(180deg, #10b981, #059669); }

        .doc-feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px -12px rgba(0,0,0,0.12);
          border-color: transparent;
        }
        .doc-feature-card:hover::before {
          width: 6px;
        }

        .doc-feature-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .doc-feature-icon {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          transition: transform 0.25s;
        }
        .doc-feature-card:hover .doc-feature-icon {
          transform: scale(1.1) rotate(-5deg);
        }
        .doc-feature-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .doc-feature-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .doc-feature-icon-green  { background: #d1fae5; color: #10b981; }

        .doc-feature-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 10px;
          border-radius: 20px;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .doc-feature-badge-blue   { background: #dbeafe; color: #1e40af; }
        .doc-feature-badge-violet { background: #ede9fe; color: #5b21b6; }
        .doc-feature-badge-green  { background: #d1fae5; color: #065f46; }

        .doc-feature-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1rem;
          margin-bottom: 8px;
        }
        .doc-feature-description {
          color: #64748b;
          font-size: 0.85rem;
          line-height: 1.55;
          margin-bottom: 18px;
        }

        /* ========== INFO BANNER ========== */
        .doc-info-banner {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 22px 26px;
          background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
          border-radius: 16px;
          border: 1px solid #fde68a;
          border-left: 4px solid #f59e0b;
          flex-wrap: wrap;
        }
        .doc-info-icon {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          flex-shrink: 0;
          box-shadow: 0 8px 20px -6px rgba(245, 158, 11, 0.5);
        }
        .doc-info-content {
          flex: 1;
          min-width: 220px;
        }
        .doc-info-title {
          font-weight: 700;
          color: #78350f;
          font-size: 0.95rem;
          margin-bottom: 4px;
        }
        .doc-info-text {
          color: #92400e;
          font-size: 0.82rem;
          line-height: 1.5;
        }
        .doc-info-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 20px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white !important;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.85rem;
          text-decoration: none;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .doc-info-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 22px -6px rgba(245, 158, 11, 0.6);
          color: white !important;
        }

        /* ========== ANIMATIONS ========== */
        @keyframes doc-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes doc-float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, -20px); }
        }
        @keyframes doc-halo {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          50% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .doc-header { padding: 20px; }
          .doc-header-title { font-size: 1.25rem; }
          .doc-hero-title { font-size: 1.5rem; }
          .doc-hero-description { font-size: 0.95rem; }
          .doc-hero-icon { width: 110px; height: 110px; font-size: 3rem; }
          .doc-hero-icon-halo { width: 140px; height: 140px; }
          .doc-support-card { justify-content: center; text-align: center; }
          .doc-support-content { text-align: center; }
          .doc-support-btn { width: 100%; justify-content: center; }
          .doc-info-banner { justify-content: center; text-align: center; }
          .doc-info-content { text-align: center; }
          .doc-info-btn { width: 100%; justify-content: center; }
          .doc-params-table thead th,
          .doc-endpoints-table thead th,
          .doc-status-table thead th { padding: 10px 12px; font-size: 0.68rem; }
          .doc-params-table tbody td,
          .doc-endpoints-table tbody td,
          .doc-status-table tbody td { padding: 10px 12px; font-size: 0.78rem; }
          .doc-code-content { font-size: 0.75rem; padding: 12px 14px; }
        }
      `}</style>
    </div>
  );
}